import React from "react";
import {
  Typography,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Box,
  Paper,
  FormHelperText,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AuthCard from "../../components/layout/AuthCard";
import { useLocation } from "react-router-dom";
import ReqMark from "./ReqMark";

export default function Step4Security({
  prevStep,
  onSubmit,
  defaultValues = {},
}) {
  const [privacyPolicy, setPrivacyPolicy] = React.useState(
    defaultValues.privacyPolicy || false
  );
  const [isAccredited, setIsAccredited] = React.useState(
    defaultValues.isAccredited || false
  );
  const [pdplConsent, setPdplConsent] = React.useState(
    defaultValues.pdplConsent || false
  );
  const [accreditedInvestor, setAccreditedInvestor] = React.useState(
    defaultValues.accreditedInvestor || false
  );
  const [idProofDocument, setIdProofDocument] = React.useState(
    defaultValues.idProofDocument || null
  );
  const [addressProofDocument, setAddressProofDocument] = React.useState(
    defaultValues.addressProofDocument || null
  );
  const [errors, setErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const role = queryParams.get("role");

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const handleFileUpload = (event, setState, errorKey) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setErrors((p) => ({ ...p, [errorKey]: "File size must not exceed 10 MB." }));
      event.target.value = "";
      return;
    }
    setState(file);
    setErrors((p) => ({ ...p, [errorKey]: "" }));
  };

  const handleCreateAccount = async () => {
    const newErrors = {};
    if (!idProofDocument) newErrors.idProofDocument = "Please upload your ID document.";
    if (!addressProofDocument) newErrors.addressProofDocument = "Please upload proof of address.";
    else if (addressProofDocument.size > MAX_FILE_SIZE) newErrors.addressProofDocument = "File size must not exceed 10 MB.";
    if (!privacyPolicy) newErrors.privacyPolicy = "You must agree to the Terms of Service and Privacy Policy.";
    if (role === "investor" && !isAccredited) newErrors.isAccredited = "You must certify your accredited investor status.";
    if (!accreditedInvestor) newErrors.accreditedInvestor = "You must consent to PDPL data processing.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const formData = new FormData();

    // TODO - Asset Owner
    // {
    //   role === "assetOwner" &&
    //     formData.append("primaryAssetType", "Real Estate");
    //   formData.append("assetsOwned", "1");
    // }

    formData.append("privacyPolicy", privacyPolicy);
    if (role === "investor") {
      formData.append("accreditedInvestor", accreditedInvestor);
    }
    // formData.append("isAccredited", isAccredited);
    formData.append("pdplConsent", pdplConsent);
    formData.append("isAccredited", isAccredited);

    if (idProofDocument) formData.append("idProofDocument", idProofDocument);
    if (addressProofDocument)
      formData.append("addressProofDocument", addressProofDocument);

    formData.forEach((value, key) => {
      console.log(`${key}: ${value}`);
    });

    if (typeof onSubmit === "function") {
      setIsSubmitting(true);
      try {
        await onSubmit(formData);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <AuthCard>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700}>
          Create Account
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{
            color: "#38bdf8",
            borderColor: "rgba(56,189,248,0.4)",
            borderRadius: "999px",
            textTransform: "none",
            px: 2,
            height: 28,
            fontSize: 12,
            "&:hover": { background: "rgba(56,189,248,0.1)" },
          }}
        >
          {role === "investor"
            ? "Investor"
            : role === "assetOwner"
            ? "Asset Owner"
            : "Admin"}
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 0.5 }}>
        Step 5 of 5 - Security & documents
      </Typography>
      <Typography variant="caption" sx={{ display: "block", color: "#f87171", mb: 2 }}>
        * Uploads and agreements marked with an asterisk are required to create your account.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
        {[1, 2, 3, 4, 5].map((bar, index) => (
          <Box
            key={bar}
            sx={{
              flex: 1,
              height: 3,
              borderRadius: 1,
              backgroundColor: index < 5 ? "#0ea5e9" : "rgba(255,255,255,0.1)",
            }}
          />
        ))}
      </Stack>

      <Stack spacing={2}>
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            ID Document
            <ReqMark />
          </Typography>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "rgba(255,255,255,0.05)",
              borderRadius: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              border: "2px dashed #38bdf8",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{
                textTransform: "none",
                color: "#38bdf8",
                borderColor: "#38bdf8",
                "&:hover": { borderColor: "#0284c7" },
              }}
              component="label"
            >
              Click to upload or drag and drop
              <input
                type="file"
                accept="image/png, image/jpeg, application/pdf"
                hidden
                onChange={(e) => handleFileUpload(e, setIdProofDocument, "idProofDocument")}
              />
            </Button>
            {idProofDocument && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {idProofDocument.name}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              PDF, JPG, PNG (Max 10MB)
            </Typography>
          </Paper>
          {errors.idProofDocument && (
            <FormHelperText error sx={{ mx: "14px", mt: 0.5 }}>{errors.idProofDocument}</FormHelperText>
          )}
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
          >
            Proof of Address
            <ReqMark />
          </Typography>
          <Paper
            sx={{
              p: 3,
              backgroundColor: "rgba(255,255,255,0.05)",
              borderRadius: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              border: "2px dashed #38bdf8",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{
                textTransform: "none",
                color: "#38bdf8",
                borderColor: "#38bdf8",
                "&:hover": { borderColor: "#0284c7" },
              }}
              component="label"
            >
              Click to upload or drag and drop
              <input
                type="file"
                accept="image/png, image/jpeg, application/pdf"
                hidden
                onChange={(e) => handleFileUpload(e, setAddressProofDocument, "addressProofDocument")}
              />
            </Button>
            {addressProofDocument && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {addressProofDocument.name}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              PDF, JPG, PNG (Max 10MB)
            </Typography>
          </Paper>
          {errors.addressProofDocument && (
            <FormHelperText error sx={{ mx: "14px", mt: 0.5 }}>{errors.addressProofDocument}</FormHelperText>
          )}
        </Box>

        <Stack spacing={2}>
          <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={privacyPolicy}
                onChange={(e) => { setPrivacyPolicy(e.target.checked); setErrors((p) => ({ ...p, privacyPolicy: "" })); }}
              />
            }
            label={
              <>
                I agree to the <Link>Terms of Service</Link> and <Link>Privacy Policy</Link>
                <ReqMark />
              </>
            }
          />
          {errors.privacyPolicy && (
            <FormHelperText error sx={{ mx: "14px" }}>{errors.privacyPolicy}</FormHelperText>
          )}
          </Box>
          {role === "investor" && (
            <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAccredited}
                  onChange={(e) => { setIsAccredited(e.target.checked); setErrors((p) => ({ ...p, isAccredited: "" })); }}
                />
              }
              label={
                <>
                  I certify that I am an accredited investor (if applicable)
                  <ReqMark />
                </>
              }
            />
            {errors.isAccredited && (
              <FormHelperText error sx={{ mx: "14px" }}>{errors.isAccredited}</FormHelperText>
            )}
            </Box>
          )}
          <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={accreditedInvestor}
                onChange={(e) => { setAccreditedInvestor(e.target.checked); setErrors((p) => ({ ...p, accreditedInvestor: "" })); }}
              />
            }
            label={
              <>
                I concent to the processing of my personal data in accordance with Saudi
                Arabia&apos;s PDPL regulations.
                <ReqMark />
              </>
            }
          />
          {errors.accreditedInvestor && (
            <FormHelperText error sx={{ mx: "14px" }}>{errors.accreditedInvestor}</FormHelperText>
          )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={pdplConsent}
                onChange={(e) => setPdplConsent(e.target.checked)}
              />
            }
            label="Send me updates about new investment opportunities"
          />
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            textTransform: "none",
          }}
          onClick={prevStep}
        >
          Back
        </Button>
        <Button
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{
            background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
          }}
          onClick={handleCreateAccount}
        >
          {isSubmitting ? "Creating Account..." : "Create Account"}
        </Button>
      </Stack>
    </AuthCard>
  );
}
